import { getTemplateOrder } from "../../../types/spine-template";

/** 尚未从缓存解析到 type 的关联模版 */
export const PENDING_RELATED_TYPE_KEY = "__pending__";

const PENDING_ORDER_RANK = 9_000_000;

function typeOrderRank(typeKey: string): number {
  if (typeKey === PENDING_RELATED_TYPE_KEY) return PENDING_ORDER_RANK;
  return getTemplateOrder(typeKey);
}

export interface RelatedTemplateTypeGroup {
  typeKey: string;
  ids: string[];
}

/**
 * 按 TEMPLATE_ORDER 分桶；桶内顺序与 orderedIds 中该类型出现的相对顺序一致。
 */
export function bucketRelatedTemplateIdsByType(
  orderedIds: string[],
  templateCache: Map<string, Partial<{ id?: string; type?: string }>>,
): RelatedTemplateTypeGroup[] {
  const bucketMap = new Map<string, string[]>();
  const firstIdx = new Map<string, number>();

  for (let i = 0; i < orderedIds.length; i++) {
    const id = orderedIds[i];
    const t = templateCache.get(id)?.type;
    const key = t ?? PENDING_RELATED_TYPE_KEY;

    if (!bucketMap.has(key)) {
      bucketMap.set(key, []);
      firstIdx.set(key, i);
    }
    bucketMap.get(key)!.push(id);
  }

  type GroupSortable = RelatedTemplateTypeGroup & {
    _first: number;
    _rank: number;
  };

  const groups: GroupSortable[] = [...bucketMap.entries()].map(
    ([typeKey, ids]) => ({
      typeKey,
      ids,
      _first: firstIdx.get(typeKey)!,
      _rank: typeOrderRank(typeKey),
    }),
  );

  groups.sort((a, b) => {
    if (a._rank !== b._rank) return a._rank - b._rank;
    return a._first - b._first;
  });

  return groups.map(({ typeKey, ids }) => ({ typeKey, ids }));
}

export function flattenRelatedTemplateGroups(
  groups: RelatedTemplateTypeGroup[],
): string[] {
  return groups.flatMap((g) => g.ids);
}

/** 保证全局顺序为：类型按 TEMPLATE_ORDER，同类型内保持当前数组中的相对顺序 */
export function normalizeRelatedTemplateIds(
  orderedIds: string[],
  templateCache: Map<string, Partial<{ id?: string; type?: string }>>,
): string[] {
  return flattenRelatedTemplateGroups(
    bucketRelatedTemplateIdsByType(orderedIds, templateCache),
  );
}
